import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Ban, X } from 'lucide-react';
import { buildAccountRestrictionsNotice } from '@/lib/whatsapp/accountRestrictions';

interface WabaRestrictionsBannerProps {
  restrictions?: unknown;
  accountName?: string | null;
  phoneNumber?: string | null;
  onDismiss?: () => void;
}

const TONE_CLASSES = {
  warning: {
    shell: 'border-[#FEDF89] bg-[#FFFAEB]',
    chip: 'bg-[#FEF0C7] text-[#B54708]',
    headline: 'text-[#93370D]',
    body: 'text-[#B54708]',
    action: 'text-[#B54708] hover:bg-[#FEF0C7]',
    panel: 'border-[#FEDF89] bg-[#FFFCF5]',
  },
  error: {
    shell: 'border-[#FECDCA] bg-[#FEF3F2]',
    chip: 'bg-[#FEE4E2] text-[#B42318]',
    headline: 'text-[#912018]',
    body: 'text-[#B42318]',
    action: 'text-[#B42318] hover:bg-[#FEE4E2]',
    panel: 'border-[#FECDCA] bg-[#FFFBFA]',
  },
} as const;

export const WabaRestrictionsBanner: React.FC<WabaRestrictionsBannerProps> = ({
  restrictions,
  accountName,
  phoneNumber,
  onDismiss,
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const notice = buildAccountRestrictionsNotice({
    restrictions,
    accountName,
    phoneNumber,
  });

  if (!notice) return null;
  if (dismissed) return null;

  const tone = TONE_CLASSES[notice.tone];

  return (
    <div
      className={`z-50 w-full border-b ${tone.shell}`}
      data-testid="waba-restrictions-banner"
      data-tone={notice.tone}
      data-restriction-count={notice.restrictions.length}
      role="status"
    >
      <div className="flex w-full items-start justify-between gap-3 px-4 py-2.5">
        <div className="flex min-w-0 items-start gap-2.5">
          <span
            className={`mt-px inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${tone.chip}`}
          >
            <Ban className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
          </span>
          <div className="min-w-0">
            <p
              className={`truncate text-[13px] font-semibold leading-5 ${tone.headline}`}
              data-testid="waba-restrictions-banner-headline"
            >
              {notice.headline}
            </p>
            <p
              className={`line-clamp-2 h-8 text-[11px] font-medium leading-4 ${tone.body}`}
              data-testid="waba-restrictions-banner-summary"
            >
              {notice.summaryLine}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            data-testid="waba-restrictions-banner-toggle"
            className={`inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[12px] font-semibold transition ${tone.action}`}
          >
            {expanded ? 'Hide details' : 'View details'}
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setDismissed(true);
              onDismiss?.();
            }}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition ${tone.action}`}
            aria-label="Dismiss banner"
            data-testid="waba-restrictions-banner-dismiss"
          >
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          className={`max-h-[45vh] overflow-y-auto border-t px-4 py-3 ${tone.panel}`}
          data-testid="waba-restrictions-banner-details"
        >
          <ul className="space-y-3">
            {notice.restrictions.map((restriction, index) => (
              <li
                key={`${restriction.type}-${index}`}
                className="min-w-0"
                data-testid="waba-restrictions-banner-item"
                data-unknown-type={restriction.isUnknownType ? 'true' : 'false'}
              >
                <p
                  className={`text-[12px] font-semibold leading-5 ${tone.headline}`}
                >
                  {restriction.summary}
                </p>
                <p
                  className={`text-[11px] font-medium leading-4 ${tone.body}`}
                  data-testid="waba-restrictions-banner-expiry"
                  data-expiry-kind={restriction.expiry.kind}
                >
                  {restriction.expiry.label}
                </p>
                {restriction.remediation && (
                  <p
                    className={`mt-1 whitespace-pre-line text-[11px] leading-4 ${tone.body}`}
                    data-testid="waba-restrictions-banner-remediation"
                  >
                    {restriction.remediation}
                  </p>
                )}
              </li>
            ))}
          </ul>

          <p
            className={`mt-3 border-t pt-2 text-[11px] font-medium leading-4 ${tone.body} ${tone.panel}`}
            data-testid="waba-restrictions-banner-appeal"
          >
            {notice.appeal.hint}{' '}
            <a
              href={notice.appeal.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-2"
              data-testid="waba-restrictions-banner-appeal-link"
            >
              {notice.appeal.label}
            </a>
          </p>
        </div>
      )}
    </div>
  );
};
